import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  ApplicationRef,
  CUSTOM_ELEMENTS_SCHEMA,
  DoBootstrap,
  Inject,
  NO_ERRORS_SCHEMA,
  NgModule
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, TitleStrategy } from '@angular/router';
import './vendor';

import { Actions, AppStore, createAppStoreFactory } from '../redux';
import applianceReducer from '../redux/reducer/appliance-reducer';
import filterReducer from '../redux/reducer/filter-reducer';
import orderFilterReducer from '../redux/reducer/order_filter-reducer';
import preferencesReducer from '../redux/reducer/preferences-reducer';
import userReducer from '../redux/reducer/user-reducer';
import userTokenReducer from '../redux/reducer/user-token-reducer';
import usersReducer from '../redux/reducer/users-reducer';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import {
  AvatarModule,
  ButtonModule,
  CollapseModule,
  DropdownModule,
  GridModule,
  HeaderModule,
  NavModule,
  NavbarModule,
  SidebarModule
} from '@coreui/angular';
import { IconModule, IconSetService } from '@coreui/icons-angular';
import { NgbModule, NgbProgressbarModule } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  AppEffects,
  AppFacadeService,
  SMARSH_CYBER_APP_CONFIG,
  SMARSH_CYBER_APP_SESSION,
  metaReducers,
  reducers
} from '@smarsh-cyber/app-state';
import {
  AppConfigService,
  AuthInterceptor,
  IAppConfiguration
} from '@smarsh-cyber/core';
import {
  CustomDropdownBtnModule,
  SmarshToasterService
} from '@smarsh-cyber/shared/component';
import { AUTH } from '@smarsh-cyber/shared/constants';
import {
  CommonHttpService,
  OnWidthChangeService,
  PageTitleStrategyService,
  ScrollListenerService,
  WindowsService
} from '@smarsh-cyber/shared/services';
import { CustomSerializer, getCurrentRoute } from '@smarsh-cyber/shared/utils';
import { ScrollListenerDirective } from 'app/shared/directive/scroll-listener.directive';
import { BlockUIModule } from 'ng-block-ui';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import selectedApplianceReducer from '../redux/reducer/selected-appliance-reducer';
import { AUTH_ROUTING } from './auth';
import { jwtOptionsFactory } from './core/auth/auth-jwt-factory';
import { BFFHttpInterceptor } from './core/blocks/interceptor/bff-http.interceptor';
import { ErrorInterceptor } from './core/error.interceptor';
import { AppErrorComponent } from './main/app-error/app-error.component';
import { SmarshCyberAppMainComponent } from './main/app-main.component';
import { AppMainFacadeService } from './main/services/app-main.facade';
import {
  ApplianceActions,
  FilterActions,
  OrderFilterActions,
  PreferencesAction,
  UserActions,
  UserTokenActions
} from './redux-export';
import { EntredaSharedModule } from './shared';
import { Idle, IdleExpiry, NgIdleModule } from '@ng-idle/core';
import { Keepalive, NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { AppSessionService } from './core/services/app-session.service';

export const appStoreFactory = createAppStoreFactory({
  userReducer,
  usersReducer,
  userTokenReducer,
  filterReducer,
  orderFilterReducer,
  preferencesReducer,
  applianceReducer,
  selectedApplianceReducer
});

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    SidebarModule,
    NavModule,
    CollapseModule,
    GridModule,
    IconModule,
    NavbarModule,
    HeaderModule,
    NavModule,
    DropdownModule,
    ButtonModule,
    HttpClientModule,
    JwtModule.forRoot({
      jwtOptionsProvider: {
        provide: JWT_OPTIONS,
        useFactory: jwtOptionsFactory,
        deps: [AppFacadeService]
      }
    }),
    ToastrModule.forRoot({
      timeOut: 8000,
      preventDuplicates: true
    }),
    RouterModule.forRoot(
      [
        {
          path: AUTH,
          children: AUTH_ROUTING
        },
        {
          path: '',
          loadChildren: () =>
            import('./layout/layout/layout.module').then((m) => m.LayoutModule)
        }
      ],
      { useHash: true }
    ),
    CustomDropdownBtnModule,
    AvatarModule,
    NgSelectModule,
    FormsModule,
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([AppEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25
    }),
    StoreRouterConnectingModule.forRoot({
      serializer: CustomSerializer
    }),
    EntredaSharedModule.forRoot(),
    NgbModule,
    BlockUIModule.forRoot(),
    NgbProgressbarModule,
    IconModule,
    NgxPermissionsModule.forRoot(),
    NgIdleModule.forRoot(),
    NgIdleKeepaliveModule.forRoot()
  ],
  //bootstrap: [SmarshCyberAppMainComponent],
  declarations: [
    SmarshCyberAppMainComponent,
    ScrollListenerDirective,
    AppErrorComponent
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: BFFHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    Actions,
    PreferencesAction,
    UserActions,
    UserTokenActions,
    FilterActions,
    OrderFilterActions,
    ApplianceActions,
    OnWidthChangeService,
    ScrollListenerService,
    SmarshToasterService,
    CommonHttpService,
    IconSetService,
    WindowsService,
    ToastrService,
    { provide: AppStore, useFactory: () => appStoreFactory() },
    {
      provide: SMARSH_CYBER_APP_CONFIG,
      useClass: AppConfigService,
      deps: [AppFacadeService, NgxPermissionsService]
    },
    {
      provide: SMARSH_CYBER_APP_SESSION,
      useClass: AppSessionService,
      deps: [Idle, Keepalive, IdleExpiry]
    },
    {
      provide: TitleStrategy,
      useClass: PageTitleStrategyService
    },
    AppMainFacadeService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class AppMainModule implements DoBootstrap {
  constructor(
    @Inject(SMARSH_CYBER_APP_CONFIG) private config: IAppConfiguration
  ) {}

  ngDoBootstrap(appRef: ApplicationRef) {
    if (getCurrentRoute().includes('/auth/login')) {
      this.config.setPortal();
      appRef.bootstrap(SmarshCyberAppMainComponent);
    } else {
      this.config.init().then(() => {
        appRef.bootstrap(SmarshCyberAppMainComponent);
      });
    }
  }
}
